import React from "react"
import { Area, AreaChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { ApolloError } from "@apollo/client"
import { theme } from "../../theme"
import {
  ChartContainer,
  ChartTooltip,
  ChartWrapper,
  EmptyState,
  Header,
  LoadingSkeleton,
  PercentageValue,
  SkeletonAreaPath,
  SkeletonAxis,
  SkeletonLabel,
  Title,
  TooltipDivider,
  TooltipLabel,
  TooltipValue,
  Wrapper
} from "./CartToOrderChart.styled"
import { ChartTimeSeriesData } from "./useChartData"
import { ToggleButton, ToggleContainer } from "../DashboardSales/OrdersOverTimeChart.styled"
import dayjs from "dayjs"

interface OrdersOverTimeChartProps {
  chartData: ChartTimeSeriesData[]
  loading: boolean
  error?: ApolloError
  interval: "hourly" | "daily"
  onIntervalChange: (interval: "hourly" | "daily") => void
  startDate: string
  endDate: string
}

export const CartToOrderChart: React.FC<OrdersOverTimeChartProps> = ({
  chartData,
  loading,
  error,
  interval,
  onIntervalChange,
  startDate,
  endDate
}) => {
  const timeRangeDays = dayjs(endDate).diff(startDate, "day")
  const getDataKeyLabel = (dataKey: string): string => {
    switch (dataKey) {
      case "orders":
        return "Orders"
      case "checkouts":
        return "Checkouts"
      case "sessions":
        return "Sessions"
      default:
        return dataKey
    }
  }

  const getDataKeyColor = (dataKey: string): string => {
    switch (dataKey) {
      case "orders":
        return theme.colors.dashboard.orders.stroke
      case "checkouts":
        return theme.colors.dashboard.checkouts.stroke
      case "sessions":
        return theme.colors.dashboard.sessions.stroke
      default:
        return theme.colors.greyDark
    }
  }

  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean
    payload?: { value: number; payload: ChartTimeSeriesData; dataKey: string }[]
  }) => {
    if (active && payload && payload.length) {
      const { sessions, checkouts, orders } = payload[0].payload
      if (sessions === 0 && checkouts === 0 && orders === 0) return null
      const checkoutFromSessions = sessions ? (checkouts / sessions) * 100 : 0
      const ordersFromSessions = sessions ? (orders / sessions) * 100 : 0
      const ordersFromCheckouts = checkouts ? (orders / checkouts) * 100 : 0

      return (
        <ChartTooltip>
          <TooltipLabel>{payload[0].payload.displayLabel}</TooltipLabel>
          {payload.map((entry, index) => (
            <TooltipValue key={index} $color={getDataKeyColor(entry.dataKey)}>
              {getDataKeyLabel(entry.dataKey)}: {entry.value}
            </TooltipValue>
          ))}
          <TooltipDivider />
          <PercentageValue>Orders / Checkouts: {ordersFromCheckouts.toFixed(1)}%</PercentageValue>
          <PercentageValue>Orders / Sessions: {ordersFromSessions.toFixed(1)}%</PercentageValue>
          <PercentageValue>Checkout / Sessions: {checkoutFromSessions.toFixed(1)}%</PercentageValue>
        </ChartTooltip>
      )
    }
    return null
  }

  const generateAreaPath = (
    points: number,
    width: number,
    height: number,
    padding: number,
    heightMultiplier: number
  ) => {
    const dataPoints = Array.from({ length: points }, (_, i) => {
      const x = (i / (points - 1)) * (width - padding * 2) + padding
      const y = (Math.sin(i * 0.5) * 15 + Math.random() * 20 + 30) * heightMultiplier
      return { x, y: height - y - padding }
    })

    const pathData = dataPoints.reduce((path, point, i) => {
      if (i === 0) {
        return `M ${point.x},${point.y}`
      }
      const prev = dataPoints[i - 1]
      const cpx1 = prev.x + (point.x - prev.x) / 3
      const cpx2 = prev.x + ((point.x - prev.x) * 2) / 3
      return `${path} C ${cpx1},${prev.y} ${cpx2},${point.y} ${point.x},${point.y}`
    }, "")

    return `${pathData} L ${width - padding},${height - padding} L ${padding},${height - padding} Z`
  }

  const renderSkeletonChart = () => {
    const points = 20
    const width = 100
    const height = 100
    const padding = 10

    const sessionsPath = generateAreaPath(points, width, height, padding, 1.2)
    const checkoutsPath = generateAreaPath(points, width, height, padding, 0.8)
    const ordersPath = generateAreaPath(points, width, height, padding, 0.5)

    return (
      <LoadingSkeleton>
        <SkeletonAreaPath viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="skeletonGradientSessions" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.colors.grey} stopOpacity={0.4} />
              <stop offset="95%" stopColor={theme.colors.greyLight} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="skeletonGradientCheckouts" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.colors.grey} stopOpacity={0.5} />
              <stop offset="95%" stopColor={theme.colors.greyLight} stopOpacity={0.08} />
            </linearGradient>
            <linearGradient id="skeletonGradientOrders" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.colors.grey} stopOpacity={0.6} />
              <stop offset="95%" stopColor={theme.colors.greyLight} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <path
            d={sessionsPath}
            fill="url(#skeletonGradientSessions)"
            stroke={theme.colors.grey}
            strokeWidth="0.5"
            className="skeleton-gradient"
          />
          <path
            d={checkoutsPath}
            fill="url(#skeletonGradientCheckouts)"
            stroke={theme.colors.grey}
            strokeWidth="0.5"
            className="skeleton-gradient"
          />
          <path
            d={ordersPath}
            fill="url(#skeletonGradientOrders)"
            stroke={theme.colors.grey}
            strokeWidth="0.5"
            className="skeleton-gradient"
          />
        </SkeletonAreaPath>
        <SkeletonAxis>
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLabel key={i} />
          ))}
        </SkeletonAxis>
      </LoadingSkeleton>
    )
  }

  return (
    <Wrapper>
      <Header>
        <Title>Cart-to-Order Funnel Over Time</Title>
        {timeRangeDays > 0 && (
          <ToggleContainer>
            <ToggleButton
              $active={interval === "hourly"}
              onClick={() => onIntervalChange("hourly")}
            >
              Hourly
            </ToggleButton>
            <ToggleButton $active={interval === "daily"} onClick={() => onIntervalChange("daily")}>
              Daily
            </ToggleButton>
          </ToggleContainer>
        )}
      </Header>
      {loading ? (
        renderSkeletonChart()
      ) : error ? (
        <EmptyState>Error loading chart data</EmptyState>
      ) : chartData.length === 0 ? (
        <EmptyState>No data available</EmptyState>
      ) : (
        <ChartWrapper $isLoading={loading}>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0
                }}
              >
                <defs>
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={theme.colors.dashboard.orders.stroke}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.colors.dashboard.orders.stroke}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient id="colorCheckouts" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={theme.colors.dashboard.checkouts.stroke}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.colors.dashboard.checkouts.stroke}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={theme.colors.dashboard.sessions.stroke}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.colors.dashboard.sessions.stroke}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="displayLabel"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  strokeWidth={1.3}
                  type="monotone"
                  dataKey="sessions"
                  stroke={theme.colors.dashboard.sessions.stroke}
                  fillOpacity={0.18}
                  fill="url(#colorSessions)"
                  name="Sessions"
                />
                <Area
                  strokeWidth={1.3}
                  type="monotone"
                  dataKey="checkouts"
                  stroke={theme.colors.dashboard.checkouts.stroke}
                  fillOpacity={0.18}
                  fill="url(#colorCheckouts)"
                  name="Checkouts"
                />
                <Area
                  strokeWidth={1.3}
                  type="monotone"
                  dataKey="orders"
                  stroke={theme.colors.dashboard.orders.stroke}
                  fillOpacity={0.18}
                  fill="url(#colorOrders)"
                  name="Orders"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartWrapper>
      )}
    </Wrapper>
  )
}
